import { useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Bookmark, Search, Sparkles } from "lucide-react";
import { articles, CATEGORIES, featuredArticles, getSaved } from "@/data/journal";
import ArticleCard from "@/components/ArticleCard";

export default function BlogArchive() {
  const [params, setParams] = useSearchParams();
  const category = params.get("cat") || "All";
  const [query, setQuery] = useState("");
  const [savedOnly, setSavedOnly] = useState(false);
  const saved = useMemo(() => getSaved(), [savedOnly]); // eslint-disable-line react-hooks/exhaustive-deps

  const setCategory = cat => {
    setSavedOnly(false);
    setParams(cat === "All" ? {} : { cat });
  };

  const visible = articles.filter(a => {
    if (savedOnly && !saved.includes(a.slug)) return false;
    if (!savedOnly && category !== "All" && a.category !== category) return false;
    const q = query.trim().toLowerCase();
    if (q && !`${a.title} ${a.category} ${a.excerpt}`.toLowerCase().includes(q)) return false;
    return true;
  });
  const showFeatured = !savedOnly && category === "All" && !query.trim();

  return (
    <main data-testid="blog-archive-page">
      <section className="journal-hero">
        <div className="journal-hero-inner">
          <div className="eyebrow gold">The ADVOKIT journal</div>
          <h1>Practical reading for the <em>working lawyer.</em></h1>
          <p data-testid="archive-tagline">Articles on litigation, corporate law, constitutional questions, careers and legal research — published fully on ADVOKIT.</p>
        </div>
      </section>

      <section className="archive-body">
        <div className="filter-bar">
          <div className="chip-row" data-testid="category-filters">
            {["All", ...CATEGORIES].map(cat => (
              <button key={cat} data-testid={`filter-${cat.toLowerCase().replace(/\s+/g, "-")}`} className={`chip ${!savedOnly && category === cat ? "active" : ""}`} onClick={() => setCategory(cat)}>{cat}</button>
            ))}
            <button data-testid="filter-saved" className={`chip chip-saved ${savedOnly ? "active" : ""}`} onClick={() => setSavedOnly(!savedOnly)}>
              <Bookmark size={12} fill={savedOnly ? "currentColor" : "none"} /> Saved ({saved.length})
            </button>
          </div>
          <div className="archive-search">
            <Search size={15} />
            <input id="blog-search" data-testid="blog-search-input" placeholder="Search by topic or title" value={query} onChange={e => setQuery(e.target.value)} />
            <span data-testid="blog-results-count">{visible.length} article{visible.length === 1 ? "" : "s"}</span>
          </div>
        </div>

        {showFeatured && (
          <div className="featured-block">
            <div className="featured-label"><Sparkles size={14} /> Featured articles</div>
            <div className="journal-grid featured-grid">
              {featuredArticles.map((a, i) => <ArticleCard key={a.slug} article={a} index={`featured-${i + 1}`} saved={saved.includes(a.slug)} />)}
            </div>
          </div>
        )}

        <div className="journal-grid" data-testid="archive-grid">
          {visible.map(a => <ArticleCard key={a.slug} article={a} index={a.slug} saved={saved.includes(a.slug)} />)}
          {visible.length === 0 && (
            <div className="blog-empty" data-testid="blog-empty-state">
              {savedOnly ? "No saved articles yet. Open an article and tap Save to build your reading list." : "No articles match that search. Try another legal topic."}
            </div>
          )}
        </div>
      </section>
    </main>
  );
}
